import React, { useState, useEffect, useCallback } from 'react';
import { Eye, X, Plus, Pencil, KeyRound, RefreshCw, Search, ShieldCheck, ShieldOff, UserX, History, Users, CheckCircle, XCircle, MoreHorizontal } from 'lucide-react';
import BASE_URL from '../services/api';

const AlertBox = ({ type, msg }) => (
  <div className={`text-sm px-4 py-2 border ${type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
    {msg}
  </div>
);

const ModalWrapper = ({ children, width = 'max-w-lg' }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className={`bg-white w-full ${width} flex flex-col max-h-[90vh]`}>
      {children}
    </div>
  </div>
);

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [portalFilter, setPortalFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [openActionsId, setOpenActionsId] = useState(null);
  const PAGE_SIZE = 10;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAddPortalModal, setShowAddPortalModal] = useState(false);
  const [showDeactivatePortalModal, setShowDeactivatePortalModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDeactivateAccountModal, setShowDeactivateAccountModal] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userPortalAccess, setUserPortalAccess] = useState([]);
  const [portalHistory, setPortalHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedPortalToDeactivate, setSelectedPortalToDeactivate] = useState('');

  const [createForm, setCreateForm] = useState({ name: '', email: '', phone_number: '', source_portal: '', role_type: 'User' });
  const [editForm, setEditForm] = useState({ name: '', email: '', phone_number: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '' });
  const [addPortalForm, setAddPortalForm] = useState({ portal_name: '', description: '' });

  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-api-key': 'A9x2mK_Lp5QvR8tN1wZ4jY7sB0uC3fE6hG9iI2kM5nO8pQ1rS4tU7vX0zW3yZ6aB',
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/users/get-users`, { headers });
      const data = await res.json();
      setUsers(data.data || []);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPortals = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/portals/get_portals`, { headers });
      const data = await res.json();
      setPortals(data.data || []);
    } catch {
      setPortals([]);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPortals();
  }, []);

  const resetFormState = () => { setFormError(''); setFormSuccess(''); };

  const handleCreateUser = async () => {
    setActionLoading(true);
    resetFormState();
    try {
      const formToSend = { ...createForm, username: createForm.email };
      const res = await fetch(`${BASE_URL}/users/sso/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formToSend),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Failed to create user'); return; }
      setFormSuccess('User created successfully');
      fetchUsers();
      setTimeout(() => {
        setShowCreateModal(false);
        setFormSuccess('');
        setCreateForm({ name: '', email: '', phone_number: '', source_portal: '', role_type: 'User' });
      }, 1200);
    } catch {
      setFormError('Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    setActionLoading(true);
    resetFormState();
    try {
      const payload = { ...editForm, username: editForm.email, portal_ids: userPortalAccess };
      const res = await fetch(`${BASE_URL}/users/update/${selectedUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Failed to update user'); return; }
      setFormSuccess('User updated successfully');
      fetchUsers();
      setTimeout(() => { setShowEditModal(false); setFormSuccess(''); }, 1200);
    } catch {
      setFormError('Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setActionLoading(true);
    resetFormState();
    try {
      const res = await fetch(`${BASE_URL}/users/change_password_portal/${selectedUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(passwordForm),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Failed to change password'); return; }
      setFormSuccess('Password changed successfully');
      setTimeout(() => {
        setShowPasswordModal(false);
        setFormSuccess('');
        setPasswordForm({ current_password: '', new_password: '' });
      }, 1200);
    } catch {
      setFormError('Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivatePortal = async () => {
    if (!selectedPortalToDeactivate) { setFormError('Please select a portal to deactivate'); return; }
    setActionLoading(true);
    resetFormState();
    try {
      const res = await fetch(`${BASE_URL}/users/${selectedUser.id}/portals/${selectedPortalToDeactivate}/deactivate`, {
        method: 'PUT',
        headers,
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Failed to deactivate portal'); return; }
      setFormSuccess('Portal deactivated successfully');
      fetchUsers();
      setTimeout(() => {
        setShowDeactivatePortalModal(false);
        setFormSuccess('');
        setSelectedPortalToDeactivate('');
      }, 1200);
    } catch {
      setFormError('Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateAccount = async () => {
    setActionLoading(true);
    resetFormState();
    try {
      const res = await fetch(`${BASE_URL}/users/${selectedUser.id}/account/deactivate`, {
        method: 'PUT',
        headers,
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Failed to deactivate account'); return; }
      setFormSuccess('Account deactivated successfully');
      fetchUsers();
      setTimeout(() => {
        setShowDeactivateAccountModal(false);
        setFormSuccess('');
      }, 1200);
    } catch {
      setFormError('Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddPortal = async () => {
    setActionLoading(true);
    resetFormState();
    try {
      const res = await fetch(`${BASE_URL}/portals/add_portal`, {
        method: 'POST',
        headers,
        body: JSON.stringify(addPortalForm),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Failed to add portal'); return; }
      setFormSuccess('Portal added successfully');
      fetchPortals();
      setTimeout(() => {
        setShowAddPortalModal(false);
        setFormSuccess('');
        setAddPortalForm({ portal_name: '', description: '' });
      }, 1200);
    } catch {
      setFormError('Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchPortalHistory = async (user) => {
    setHistoryLoading(true);
    setPortalHistory([]);
    try {
      const res = await fetch(`${BASE_URL}/users/${user.id}/portal-history`, { headers });
      const data = await res.json();
      setPortalHistory(data.data?.portalHistory || []);
    } catch {
      setPortalHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getActivePortals = (user) => {
    const seen = new Set();
    return (user.portals || []).filter(p => {
      if (p.end_date === null && !seen.has(p.portal_id)) {
        seen.add(p.portal_id);
        return true;
      }
      return false;
    });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({ name: user.name, email: user.email, phone_number: user.phone_number || '' });
    const activePortalIds = [...new Set(
      (user.portals || []).filter(p => p.end_date === null).map(p => parseInt(p.portal_id))
    )];
    setUserPortalAccess(activePortalIds);
    resetFormState();
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordForm({ current_password: '', new_password: '' });
    resetFormState();
    setShowPasswordModal(true);
  };

  const openAccessModal = (user) => {
    setSelectedUser(user);
    const activePortalIds = [...new Set(
      (user.portals || []).filter(p => p.end_date === null).map(p => p.portal_id)
    )];
    setUserPortalAccess(activePortalIds);
    resetFormState();
    setShowAccessModal(true);
  };

  const openDeactivatePortalModal = (user) => {
    setSelectedUser(user);
    setSelectedPortalToDeactivate('');
    resetFormState();
    setShowDeactivatePortalModal(true);
  };

  const openHistoryModal = async (user) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
    await fetchPortalHistory(user);
  };

  const openDeactivateAccountModal = (user) => {
    setSelectedUser(user);
    resetFormState();
    setShowDeactivateAccountModal(true);
  };

const togglePortalAccess = (portalId) => {
    const id = parseInt(portalId);
    setUserPortalAccess(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

const handleUpdatePortalAccess = async () => {
    setActionLoading(true);
    resetFormState();
    try {
      const res = await fetch(`${BASE_URL}/users/update/${selectedUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ portal_ids: userPortalAccess }),
      });
      const data = await res.json();
      if (!res.ok) { setFormError(data.message || 'Failed to update portal access'); return; }
      setFormSuccess('Portal access updated successfully');
      fetchUsers();
      setTimeout(() => {
        setShowAccessModal(false);
        setFormSuccess('');
      }, 1200);
    } catch {
      setFormError('Something went wrong');
    } finally {
      setActionLoading(false);
    }
  };

  // KPIs
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.account_status?.toLowerCase() === 'active').length;
  const inactiveUsers = users.filter(u => u.account_status?.toLowerCase() === 'inactive').length;
  const totalPortalAssignments = users.reduce((acc, u) => acc + getActivePortals(u).length, 0);

  // Filtered users
  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());

    const matchesPortal = portalFilter
      ? getActivePortals(u).some(p => p.portal_id === parseInt(portalFilter))
      : true;

    return matchesSearch && matchesPortal;
  });

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  useEffect(() => { setCurrentPage(1); }, [search, portalFilter]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [totalPages, currentPage]);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const paginatedUsers = filteredUsers.slice(startIdx, startIdx + PAGE_SIZE);

  const inputClass = "w-full px-3 py-2.5 border border-gray-200 focus:outline-none focus:border-[#3d82c4] text-sm text-gray-800 bg-white";
  const labelClass = "block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
          <p className="text-sm text-gray-600 mt-1">Manage users and their portal access</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowAddPortalModal(true); resetFormState(); }}
            className="px-4 py-2 border border-[#3d82c4] text-[#3d82c4] text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Portal
          </button>
          <button
            onClick={() => { setShowCreateModal(true); resetFormState(); }}
            className="px-4 py-2 bg-[#3d82c4] text-white text-sm font-medium hover:bg-[#2d6fad] transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Create User
          </button>
        </div>
      </div>

      {/* KPIs */}
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
{[
  { label: 'Total Users', value: totalUsers, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  { label: 'Active Users', value: activeUsers, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  { label: 'Inactive Users', value: inactiveUsers, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
].map(({ label, value, icon: Icon, color, bg, border }) => (
          <div key={label} className={`${bg} border ${border} p-4 flex items-center gap-4`}>
            <div className={`w-10 h-10 ${bg} border ${border} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Portal Filter & Refresh */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 focus:outline-none focus:border-[#3d82c4] text-sm"
          />
        </div>
        <select
          value={portalFilter}
          onChange={e => setPortalFilter(e.target.value)}
          className="py-2.5 px-3 border border-gray-200 focus:outline-none focus:border-[#3d82c4] text-sm text-gray-700 bg-white min-w-[200px]"
        >
          <option value="">All Portals</option>
          {portals.map(p => (
            <option key={p.id} value={p.id}>{p.portal_name}</option>
          ))}
        </select>
        {(search || portalFilter) && (
          <button
            onClick={() => { setSearch(''); setPortalFilter(''); }}
            className="px-3 py-2.5 text-sm text-gray-500 border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        )}
        <button onClick={fetchUsers} className="p-2.5 border border-gray-200 hover:bg-gray-50 transition-colors text-gray-500">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {portalFilter && (
          <span className="text-xs text-gray-500">
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} with access to <strong>{portals.find(p => p.id === parseInt(portalFilter))?.portal_name}</strong>
          </span>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['#', 'Name', 'Email', 'Phone', 'Status', 'Active Portals', 'Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Loading users...</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No users found.</td></tr>
              ) : (
                paginatedUsers.map((user, idx) => {
                  const activePortals = getActivePortals(user);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 text-sm text-gray-500">{startIdx + idx + 1}</td>
                      <td className="px-5 py-3">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-xs text-gray-400">{user.username}</div>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-700">{user.email}</td>
                      <td className="px-5 py-3 text-sm text-gray-700">{user.phone_number || '-'}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-1 text-xs font-semibold ${user.account_status?.toLowerCase() === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {user.account_status}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {activePortals.length === 0 ? (
                            <span className="text-xs text-gray-400">None</span>
                          ) : activePortals.map(p => (
                            <span key={p.portal_id} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs border border-blue-100">
                              {p.portal_name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="relative inline-block">
                          <button
                            onClick={() => setOpenActionsId(openActionsId === user.id ? null : user.id)}
                            className="p-1.5 text-gray-500 hover:bg-gray-100 border border-gray-200 transition-colors"
                            title="Actions"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          {openActionsId === user.id && (
                            <>
                              <div
                                className="fixed inset-0 z-30"
                                onClick={() => setOpenActionsId(null)}
                              />
                              <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 shadow-lg z-40">
                                <button onClick={() => { setOpenActionsId(null); openEditModal(user); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left">
                                  <Pencil className="w-3.5 h-3.5 text-blue-600" /> Edit
                                </button>
                                <button onClick={() => { setOpenActionsId(null); openPasswordModal(user); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left">
                                  <KeyRound className="w-3.5 h-3.5 text-yellow-600" /> Change Password
                                </button>
                                <button onClick={() => { setOpenActionsId(null); openDeactivatePortalModal(user); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left">
                                  <ShieldOff className="w-3.5 h-3.5 text-orange-600" /> Deactivate Portal
                                </button>
                                <button onClick={() => { setOpenActionsId(null); openHistoryModal(user); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 text-left">
                                  <History className="w-3.5 h-3.5 text-purple-600" /> Portal History
                                </button>
                                <button
                                  onClick={() => { setOpenActionsId(null); openDeactivateAccountModal(user); }}
                                  disabled={user.account_status?.toLowerCase() === 'inactive'}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-700 hover:bg-red-50 text-left border-t border-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <UserX className="w-3.5 h-3.5 text-red-600" /> Deactivate Account
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 flex-wrap gap-2">
            <span className="text-xs text-gray-600">
              Showing <strong>{startIdx + 1}</strong>–<strong>{Math.min(startIdx + PAGE_SIZE, filteredUsers.length)}</strong> of <strong>{filteredUsers.length}</strong>
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="px-3 py-1.5 text-xs text-gray-700">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── CREATE USER MODAL ── */}
      {showCreateModal && (
        <ModalWrapper>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Create User</h2>
            <button onClick={() => setShowCreateModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {formError && <AlertBox type="error" msg={formError} />}
            {formSuccess && <AlertBox type="success" msg={formSuccess} />}
            <div>
              <label className={labelClass}>Full Name</label>
              <input type="text" value={createForm.name} onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))} className={inputClass} placeholder="Enter full name" />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" value={createForm.email} onChange={e => setCreateForm(prev => ({ ...prev, email: e.target.value }))} className={inputClass} placeholder="Enter email" />
            </div>
            <div>
              <label className={labelClass}>Phone Number</label>
              <input type="text" value={createForm.phone_number} onChange={e => setCreateForm(prev => ({ ...prev, phone_number: e.target.value }))} className={inputClass} placeholder="Enter phone number" />
            </div>
            <div>
              <label className={labelClass}>Source Portal</label>
              <select value={createForm.source_portal} onChange={e => setCreateForm(prev => ({ ...prev, source_portal: e.target.value }))} className={inputClass}>
                <option value="">Select portal</option>
                {portals.map(p => <option key={p.id} value={p.portal_name}>{p.portal_name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Role Type</label>
              <select value={createForm.role_type} onChange={e => setCreateForm(prev => ({ ...prev, role_type: e.target.value }))} className={inputClass}>
                <option value="User">User</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleCreateUser} disabled={actionLoading} className="px-4 py-2 bg-[#3d82c4] text-white text-sm font-medium hover:bg-[#2d6fad] transition-colors disabled:opacity-60">
              {actionLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* ── EDIT USER MODAL ── */}
      {showEditModal && selectedUser && (
        <ModalWrapper>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Edit User</h2>
              <p className="text-xs text-gray-500 mt-0.5">{selectedUser.name}</p>
            </div>
            <button onClick={() => setShowEditModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {formError && <AlertBox type="error" msg={formError} />}
            {formSuccess && <AlertBox type="success" msg={formSuccess} />}
            <div>
              <label className={labelClass}>Full Name</label>
              <input type="text" value={editForm.name} onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))} className={inputClass} placeholder="Enter full name" />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input type="email" value={editForm.email} onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))} className={inputClass} placeholder="Enter email" />
            </div>
            <div>
              <label className={labelClass}>Phone Number</label>
              <input type="text" value={editForm.phone_number} onChange={e => setEditForm(prev => ({ ...prev, phone_number: e.target.value }))} className={inputClass} placeholder="Enter phone number" />
            </div>
            <div>
              <label className={labelClass}>Username</label>
              <input type="text" value={editForm.email} disabled className={`${inputClass} bg-gray-50 text-gray-400 cursor-not-allowed`} />
              <p className="text-xs text-gray-400 mt-1">Username is same as email and cannot be changed.</p>
            </div>
            <div>
              <label className={labelClass}>Portal Access</label>
              {portals.length === 0 ? (
                <p className="text-xs text-gray-400">No portals available.</p>
              ) : (
                <div className="space-y-2 mt-1">
                  {portals.map(portal => {
                    const hasAccess = userPortalAccess.includes(portal.id);
                    return (
                      <div key={portal.id} className="flex items-center justify-between px-3 py-2 border border-gray-200 hover:bg-gray-50">
                        <span className="text-sm text-gray-800">{portal.portal_name}</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" checked={hasAccess} onChange={() => togglePortalAccess(portal.id)} className="sr-only peer" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#3d82c4] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:h-5 after:w-5 after:transition-all peer-checked:bg-[#3d82c4]"></div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleUpdateUser} disabled={actionLoading} className="px-4 py-2 bg-[#3d82c4] text-white text-sm font-medium hover:bg-[#2d6fad] transition-colors disabled:opacity-60">
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* ── CHANGE PASSWORD MODAL ── */}
      {showPasswordModal && selectedUser && (
        <ModalWrapper width="max-w-md">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Change Password</h2>
              <p className="text-xs text-gray-500 mt-0.5">{selectedUser.name}</p>
            </div>
            <button onClick={() => setShowPasswordModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="p-6 space-y-4">
            {formError && <AlertBox type="error" msg={formError} />}
            {formSuccess && <AlertBox type="success" msg={formSuccess} />}
            <div>
              <label className={labelClass}>Current Password</label>
              <input type="password" value={passwordForm.current_password} onChange={e => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))} className={inputClass} placeholder="Enter current password" />
            </div>
            <div>
              <label className={labelClass}>New Password</label>
              <input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))} className={inputClass} placeholder="Enter new password" />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={() => setShowPasswordModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleChangePassword} disabled={actionLoading} className="px-4 py-2 bg-[#3d82c4] text-white text-sm font-medium hover:bg-[#2d6fad] transition-colors disabled:opacity-60">
              {actionLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* ── MANAGE ACCESS MODAL ── */}
      {showAccessModal && selectedUser && (
        <ModalWrapper width="max-w-2xl">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Manage Portal Access</h2>
              <p className="text-xs text-gray-500 mt-0.5">{selectedUser.name} — {selectedUser.email}</p>
            </div>
            <button onClick={() => setShowAccessModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            {portals.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No portals available.</p>
            ) : (
              <div className="space-y-3">
                {portals.map(portal => {
                  const hasAccess = userPortalAccess.includes(portal.id);
                  return (
                    <div key={portal.id} className="flex items-center justify-between p-4 border border-gray-200 hover:bg-gray-50 transition-colors">
                      <span className="text-sm font-medium text-gray-800">{portal.portal_name}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={hasAccess} onChange={() => togglePortalAccess(portal.id)} className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#3d82c4] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:h-5 after:w-5 after:transition-all peer-checked:bg-[#3d82c4]"></div>
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={() => setShowAccessModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleUpdatePortalAccess} disabled={actionLoading} className="px-4 py-2 bg-[#3d82c4] text-white text-sm font-medium hover:bg-[#2d6fad] transition-colors disabled:opacity-60">
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* ── DEACTIVATE PORTAL MODAL ── */}
      {showDeactivatePortalModal && selectedUser && (
        <ModalWrapper width="max-w-md">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Deactivate Portal</h2>
              <p className="text-xs text-gray-500 mt-0.5">{selectedUser.name}</p>
            </div>
            <button onClick={() => setShowDeactivatePortalModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="p-6 space-y-4">
            {formError && <AlertBox type="error" msg={formError} />}
            {formSuccess && <AlertBox type="success" msg={formSuccess} />}
            <div>
              <label className={labelClass}>Select Portal to Deactivate</label>
              <select value={selectedPortalToDeactivate} onChange={e => setSelectedPortalToDeactivate(e.target.value)} className={inputClass}>
                <option value="">Select portal</option>
                {getActivePortals(selectedUser).map(p => (
                  <option key={p.portal_id} value={p.portal_id}>{p.portal_name}</option>
                ))}
              </select>
              {getActivePortals(selectedUser).length === 0 && (
                <p className="text-xs text-gray-400 mt-2">No active portals for this user.</p>
              )}
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={() => setShowDeactivatePortalModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleDeactivatePortal} disabled={actionLoading || !selectedPortalToDeactivate} className="px-4 py-2 bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-60">
              {actionLoading ? 'Deactivating...' : 'Deactivate Portal'}
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* ── PORTAL HISTORY MODAL ── */}
      {showHistoryModal && selectedUser && (
        <ModalWrapper width="max-w-3xl">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Portal History</h2>
              <p className="text-xs text-gray-500 mt-0.5">{selectedUser.name} — {selectedUser.email}</p>
            </div>
            <button onClick={() => setShowHistoryModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            {historyLoading ? (
              <div className="text-center py-10 text-gray-400 text-sm">Loading history...</div>
            ) : portalHistory.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No portal history found.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Portal', 'Created By', 'Start Date', 'End Date', 'Status'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {portalHistory.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{item.portal_name}</td>
                      <td className="px-4 py-3 text-gray-600">{item.created_by || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{item.start_date ? new Date(item.start_date).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{item.end_date ? new Date(item.end_date).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold ${item.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button onClick={() => setShowHistoryModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Close</button>
          </div>
        </ModalWrapper>
      )}

      {/* ── DEACTIVATE ACCOUNT MODAL ── */}
      {showDeactivateAccountModal && selectedUser && (
        <ModalWrapper width="max-w-md">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Deactivate Account</h2>
            <button onClick={() => setShowDeactivateAccountModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="p-6 space-y-4">
            {formError && <AlertBox type="error" msg={formError} />}
            {formSuccess && <AlertBox type="success" msg={formSuccess} />}
            <div className="bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-700">
                Are you sure you want to deactivate the account for <strong>{selectedUser.name}</strong>? This will revoke their access to all portals.
              </p>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={() => setShowDeactivateAccountModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleDeactivateAccount} disabled={actionLoading} className="px-4 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60">
              {actionLoading ? 'Deactivating...' : 'Yes, Deactivate'}
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* ── ADD PORTAL MODAL ── */}
      {showAddPortalModal && (
        <ModalWrapper width="max-w-md">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Add Portal</h2>
            <button onClick={() => setShowAddPortalModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
          </div>
          <div className="p-6 space-y-4">
            {formError && <AlertBox type="error" msg={formError} />}
            {formSuccess && <AlertBox type="success" msg={formSuccess} />}
            <div>
              <label className={labelClass}>Portal Name</label>
              <input type="text" value={addPortalForm.portal_name} onChange={e => setAddPortalForm(prev => ({ ...prev, portal_name: e.target.value }))} className={inputClass} placeholder="Enter portal name" />
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea value={addPortalForm.description} onChange={e => setAddPortalForm(prev => ({ ...prev, description: e.target.value }))} className={`${inputClass} resize-none`} rows={3} placeholder="Enter description" />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button onClick={() => setShowAddPortalModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleAddPortal} disabled={actionLoading} className="px-4 py-2 bg-[#3d82c4] text-white text-sm font-medium hover:bg-[#2d6fad] transition-colors disabled:opacity-60">
              {actionLoading ? 'Adding...' : 'Add Portal'}
            </button>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

export default UserManagement;